// Page-wide UI state shared between the tree panel and the detail panel.
// Server data (tree, item details) lives in React Query — this store only
// tracks transient screen state per PRD 04 §5.
//
// Slice 3: selection + expansion.
// Slice 12: dirty flag drives the post-save info modal.
// Slice 13: navigation guard — `requestSelect` defers the actual selection
//   change when the form is dirty so the page can show a confirm modal.

import { create } from 'zustand';

// PRD §5 — the right panel routes on the type of the selected node.
export type SelectedNode =
  | null
  | { type: 'domain'; id: number }
  | { type: 'subArea'; id: number; domainId: number }
  | { type: 'item'; id: number; domainId: number; subAreaId: number };

// SHCT-388 / ADM.034 — "persist on Save, not on Add". Clicking Add no longer
// hits the backend; it parks a local, not-yet-persisted node here. The node is
// merged into the tree for display (negative `tempId` so it never collides with
// a real server id) and written to the backend only when the user clicks Save.
// Leaving it unsaved (navigating away) discards it. One draft at a time.
export type DraftNode =
  | { type: 'domain'; tempId: number; name: string }
  | { type: 'subArea'; tempId: number; domainId: number; name: string }
  | { type: 'item'; tempId: number; domainId: number; subAreaId: number; name: string };

// Monotonically-decreasing temp ids (-1, -2, …) for draft nodes. Never reset so
// two Adds in one session can't reuse an id.
let tempSeq = 0;
export function nextTempId(): number {
  tempSeq -= 1;
  return tempSeq;
}

// A draft is only valid while it is the selected node. Any selection change to a
// different node discards it (prevents an orphan draft lingering in the tree).
function keepDraftIfSelected(draft: DraftNode | null, node: SelectedNode): DraftNode | null {
  if (draft === null) return null;
  if (node !== null && node.id === draft.tempId) return draft;
  return null;
}

// True when both refer to the same tree node. Used to keep `requestSelect`
// idempotent — clicking the row that's already selected should NOT pop the
// unsaved-changes modal.
function selectedEqual(a: SelectedNode, b: SelectedNode): boolean {
  if (a === null || b === null) return a === b;
  if (a.type !== b.type) return false;
  return a.id === b.id;
}

interface ProItemDesignPageState {
  selected: SelectedNode;
  // Expanded toggles keyed by 'd{id}' (domain) and 's{id}' (sub-area). Item
  // nodes do not expand. Default false — a domain becomes expanded the first
  // time the user clicks it or one of its descendants is auto-selected.
  expanded: Record<string, boolean>;
  // Set to true while the right-panel form has unsaved input.
  dirty: boolean;
  // When non-null, the unsaved-changes modal is open and this is the node
  // we'll move to once the user discards. Cleared on Stay.
  pendingNavigation: SelectedNode;
  // Node id whose name input should grab focus once its detail panel mounts.
  // Set by the create handlers so a freshly-added Area/Sub-area/Item lands
  // with the cursor in the name field (QC PID-005/013/018); consumed + cleared
  // by the detail panel.
  focusRequestId: number | null;
  // SHCT-388 — the not-yet-persisted node created by an Add click. Merged into
  // the tree for display; persisted (via the create endpoint) only on Save.
  draft: DraftNode | null;

  // Direct selection — bypasses the dirty guard. Used by mutation handlers
  // (post-save / post-delete) where the data is already in a consistent
  // state and we want to clear the form without prompting.
  select: (node: SelectedNode) => void;
  // Guarded selection — checks dirty and parks the target in
  // `pendingNavigation` if needed. Used by every user-initiated nav (tree
  // row click, breadcrumb, mini-table row click).
  requestSelect: (node: SelectedNode) => void;
  // Modal actions.
  confirmNavigation: () => void;
  cancelNavigation: () => void;

  toggleExpand: (key: string) => void;
  setExpanded: (key: string, value: boolean) => void;
  setDirty: (value: boolean) => void;
  // Request/consume the one-shot name-input focus for a just-created node.
  requestFocus: (id: number) => void;
  consumeFocus: (id: number) => void;
  // SHCT-388 — park / discard the not-yet-persisted Add draft.
  startDraft: (node: DraftNode) => void;
  clearDraft: () => void;
  reset: () => void;
}

const INITIAL: Pick<
  ProItemDesignPageState,
  'selected' | 'expanded' | 'dirty' | 'pendingNavigation' | 'focusRequestId' | 'draft'
> = {
  selected: null,
  expanded: {},
  dirty: false,
  pendingNavigation: null,
  focusRequestId: null,
  draft: null,
};

export const useProItemDesignPageStore = create<ProItemDesignPageState>((set) => ({
  ...INITIAL,
  select: (node) =>
    set((s) => ({
      selected: node,
      dirty: false,
      pendingNavigation: null,
      draft: keepDraftIfSelected(s.draft, node),
    })),
  requestSelect: (node) =>
    set((s) => {
      if (selectedEqual(s.selected, node)) return s;
      if (!s.dirty) {
        return {
          selected: node,
          pendingNavigation: null,
          draft: keepDraftIfSelected(s.draft, node),
        };
      }
      return { pendingNavigation: node };
    }),
  confirmNavigation: () =>
    set((s) =>
      s.pendingNavigation === null
        ? s
        : {
            selected: s.pendingNavigation,
            pendingNavigation: null,
            dirty: false,
            draft: keepDraftIfSelected(s.draft, s.pendingNavigation),
          },
    ),
  cancelNavigation: () => set({ pendingNavigation: null }),
  toggleExpand: (key) =>
    set((s) => ({ expanded: { ...s.expanded, [key]: !s.expanded[key] } })),
  setExpanded: (key, value) =>
    set((s) => (s.expanded[key] === value ? s : { expanded: { ...s.expanded, [key]: value } })),
  setDirty: (value) => set({ dirty: value }),
  requestFocus: (id) => set({ focusRequestId: id }),
  consumeFocus: (id) =>
    set((s) => (s.focusRequestId === id ? { focusRequestId: null } : s)),
  startDraft: (node) => set({ draft: node }),
  clearDraft: () => set({ draft: null }),
  reset: () => set(INITIAL),
}));

// Stable helper keys so callers don't repeatedly build template strings.
export const domainKey = (id: number) => `d${id}`;
export const subAreaKey = (id: number) => `s${id}`;
